import { TutorialAudience, TutorialDefinition } from "./types";

export interface EligibilityUser {
  createdAt: string | Date | null | undefined;
  seen: string[];
}

/**
 * Pure function — returns the subset of tutorials a user is eligible to see.
 *
 * Rules:
 *   - Only tutorials for this `audience` (officer app vs agency portal).
 *   - Exclude tutorials already in `seen`.
 *   - "new-feature" tutorials only show to users who joined BEFORE the
 *     tutorial's releasedAt. (Brand-new signups don't see "new feature" tours
 *     for things that were already there when they joined.)
 *   - "onboarding" tutorials show to anyone who hasn't seen them yet,
 *     regardless of join date.
 */
export const computeEligibleTutorials = (
  all: TutorialDefinition[],
  user: EligibilityUser,
  audience: TutorialAudience = "officer",
): TutorialDefinition[] => {
  const createdAtMs = user.createdAt
    ? new Date(user.createdAt).getTime()
    : 0;

  return all.filter((t) => {
    // Officer tours never run in the agency portal and vice versa.
    if ((t.audience ?? "officer") !== audience) return false;
    if (user.seen.includes(t.id)) return false;
    if (t.category === "new-feature") {
      return createdAtMs > 0 && createdAtMs < Date.parse(t.releasedAt);
    }
    return true;
  });
};
